import { fulfillmentTypes } from '../constants/commonConstants';

type AttributeValue = {
	value: string | string[];
};

type ProductAttribute = {
	identifier: string;
	values: AttributeValue[];
};

/**
 * Determines the fulfillment type for a product based on its attributes and associated order ribbons.
 * 'Dropship' takes precedence over 'Online Only' when both ribbons are present.
 *
 * Lives in `shared/` so both presentation and integration layers can consume it.
 */
export const getFulfillmentType = (
	attributes: ProductAttribute[] | undefined,
	orderRibbons: string[] | undefined
): string | undefined => {
	if (!attributes) {
		return undefined;
	}

	const fulfillmentAttr = attributes.find((attr) => attr?.identifier === 'FULFILLMENT_TYPE');
	const fulfillmentValues = fulfillmentAttr?.values?.[0]?.value;

	if (!fulfillmentValues) {
		return undefined;
	}

	const fulfillmentTypes = Array.isArray(fulfillmentValues)
		? fulfillmentValues
		: [fulfillmentValues];
	if (fulfillmentTypes.length === 0) {
		return undefined;
	}

	let result: string | undefined = fulfillmentTypes[0];

	if (
		orderRibbons?.includes('ONLINE_ONLY_SELLING_STORE') &&
		fulfillmentTypes.includes('Online Only')
	) {
		result = 'Online Only';
	}

	if (
		orderRibbons?.includes('DROPSHIP_ONLY_SELLING_STORE') &&
		fulfillmentTypes.includes('Dropship')
	) {
		result = 'Dropship';
	}

	return result;
};

/**
 * Dropship / Special Order products are fulfilled from the market, not the
 * selected store, so the PDP's pickup option (and the add-to-cart gate that
 * follows it) must read the market-based delivery option instead of the store one.
 */
export const isMarketFulfilledProduct = (
	product: { attributes?: ProductAttribute[]; ribbons?: { identifier: string }[] } | undefined
): boolean => {
	const fulfillmentType = getFulfillmentType(
		product?.attributes,
		(product?.ribbons ?? []).map(({ identifier }) => identifier)
	);
	return (
		fulfillmentType === fulfillmentTypes.DROPSHIP ||
		fulfillmentType === fulfillmentTypes.SPECIAL_ORDER
	);
};
